import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import {
  ArrowLeft, Search, Download, FileDown, AlertTriangle,
  ArrowUp, ArrowDown, X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Pill, Spinner, EmptyState } from "@/components/ui";
import { dated } from "@/lib/exportCsv";
import { DATASET_BY_ID, type ReportDataset, type ColType } from "./reportDatasets";
import { isPresenting, MONEY_MASK } from "@/lib/presenting";

// =============================================================================
// DataExplorerScreen — T-392  (/reports/explore/:datasetId)
// Generic find-filter-export over any registered dataset. Preview shows the
// first 200 rows live; Export Excel pulls the FULL filtered set (paged,
// capped at 20k rows) into a two-sheet workbook (Report info + Data,
// autofiltered). Manager+ (RLS enforces underneath regardless).
// =============================================================================

const PREVIEW_ROWS = 200;
const EXPORT_CAP   = 20000;
const PAGE         = 1000;

// strip chars that would break the PostgREST or() filter string
const cleanSearch = (s: string) => s.replace(/[,()%]/g, " ").trim();

function fmtCell(v: unknown, type: ColType): string {
  if (v == null || v === "") return "—";
  switch (type) {
    case "money":
      // T-508 — Presenting mode. Only the on-screen table masks; the CSV/xlsx
      // export path builds its own strings and still carries real numbers.
      return isPresenting() ? MONEY_MASK
        : Number(v).toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 2 });
    case "num":
      return Number(v).toLocaleString("en-AU", { maximumFractionDigits: 2 });
    case "date":
      return new Date(String(v)).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
    case "datetime":
      return new Date(String(v)).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    case "bool":
      return v ? "✓" : "—";
    default:
      return String(v);
  }
}

function buildQuery(def: ReportDataset, f: {
  search: string; dateFrom: string; dateTo: string; status: string;
}) {
  let q = supabase.from(def.table).select(def.select, { count: "exact" });
  const term = cleanSearch(f.search);
  if (term && def.searchFields.length > 0) {
    q = q.or(def.searchFields.map(fld => `${fld}.ilike.%${term}%`).join(","));
  }
  if (term && def.searchEmbed) {
    q = q.or(def.searchEmbed.fields.map(fld => `${fld}.ilike.%${term}%`).join(","), { referencedTable: def.searchEmbed.table });
  }
  if (def.dateField) {
    if (f.dateFrom) q = q.gte(def.dateField.key, f.dateFrom);
    if (f.dateTo)   q = q.lte(def.dateField.key, f.dateTo);
  }
  if (def.statusField && f.status) {
    q = def.statusField.isBool
      ? q.eq(def.statusField.key, f.status === "true")
      : q.eq(def.statusField.key, f.status);
  }
  return q.order(def.defaultOrder.field, { ascending: def.defaultOrder.asc });
}

export default function DataExplorerScreen() {
  const { datasetId } = useParams<{ datasetId: string }>();
  const user = useRequireAuth();
  const canView = hasRole(user, "manager");
  const def = datasetId ? DATASET_BY_ID[datasetId] : undefined;

  const [search,    setSearch]    = useState("");
  const [debounced, setDebounced] = useState("");
  const [dateFrom,  setDateFrom]  = useState("");
  const [dateTo,    setDateTo]    = useState("");
  const [status,    setStatus]    = useState("");
  const [sort,      setSort]      = useState<{ key: string; asc: boolean } | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);
  const [exportErr, setExportErr] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const filters = { search: debounced, dateFrom, dateTo, status };
  const hasFilters = !!(debounced || dateFrom || dateTo || status);

  const { data, isLoading, error } = useQuery({
    queryKey: ["report_explore", def?.id, debounced, dateFrom, dateTo, status],
    enabled: !!def && canView,
    queryFn: async () => {
      const { data: rows, count, error } = await buildQuery(def!, filters).range(0, PREVIEW_ROWS - 1);
      if (error) throw error;
      let list = (rows ?? []) as any[];
      if (def!.hydrate) list = await def!.hydrate(list);
      return { rows: list.map(def!.flatten), count: count ?? list.length };
    },
  });

  const previewRows = useMemo(() => {
    const rows = data?.rows ?? [];
    if (!sort) return rows;
    const dir = sort.asc ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [data, sort]);

  if (!def) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><div className="p-6">
          <EmptyState title="Unknown dataset" description="That report doesn't exist. Head back to the Reports hub." />
          <Link to="/reports" className="text-accent text-sm hover:underline mt-2 inline-block">← Reports hub</Link>
        </div></Card>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><div className="p-6 text-sm text-text-muted">Reports are restricted to managers and directors.</div></Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Full-set export (paged fetch → flatten → XLSX / CSV)
  // ---------------------------------------------------------------------------
  async function fetchAll(): Promise<Record<string, unknown>[]> {
    const out: any[] = [];
    for (let from = 0; from < EXPORT_CAP; from += PAGE) {
      const { data: rows, error } = await buildQuery(def!, filters).range(from, from + PAGE - 1);
      if (error) throw error;
      out.push(...(rows ?? []));
      if (!rows || rows.length < PAGE) break;
    }
    const hydrated = def!.hydrate ? await def!.hydrate(out) : out;
    return hydrated.map(def!.flatten);
  }

  function filterLine(): string {
    const bits: string[] = [];
    if (debounced) bits.push(`search "${debounced}"`);
    if (def!.dateField && (dateFrom || dateTo)) bits.push(`${def!.dateField.label} ${dateFrom || "…"} → ${dateTo || "…"}`);
    if (def!.statusField && status) bits.push(`${def!.statusField.label}: ${status}`);
    return bits.length ? bits.join(" · ") : "No filters — full dataset";
  }

  async function exportXlsx() {
    setExporting("xlsx"); setExportErr(null);
    try {
      const rows = await fetchAll();
      const headers = def!.columns.map(c => c.label);
      const aoa: unknown[][] = [headers, ...rows.map(r => def!.columns.map(c => {
        const v = r[c.key];
        if (c.type === "bool") return v ? "Yes" : "No";
        return v ?? "";
      }))];
      const wb = XLSX.utils.book_new();
      const info = XLSX.utils.aoa_to_sheet([
        ["Coretel Ops — data export"],
        ["Dataset",   def!.label],
        ["Generated", new Date().toLocaleString("en-AU")],
        ["Filters",   filterLine()],
        ["Rows",      rows.length + (rows.length >= EXPORT_CAP ? " (capped — narrow the filters)" : "")],
      ]);
      info["!cols"] = [{ wch: 12 }, { wch: 70 }];
      XLSX.utils.book_append_sheet(wb, info, "Report info");
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = def!.columns.map(c => ({ wch: Math.max(c.label.length + 2, c.type === "text" ? 24 : 14) }));
      ws["!autofilter"] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}${aoa.length}` };
      XLSX.utils.book_append_sheet(wb, ws, "Data");
      XLSX.writeFile(wb, `${dated(`coretel_${def!.id}`)}.xlsx`);
    } catch (e: any) {
      setExportErr(e?.message ?? "Export failed");
    } finally {
      setExporting(null);
    }
  }

  async function exportCsvFull() {
    setExporting("csv"); setExportErr(null);
    try {
      const rows = await fetchAll();
      const { exportCsv } = await import("@/lib/exportCsv");
      exportCsv(dated(`coretel_${def!.id}`), rows, def!.columns.map(c => ({
        header: c.label,
        value: (r: Record<string, unknown>) => c.type === "bool" ? (r[c.key] ? "Yes" : "No") : r[c.key],
      })));
    } catch (e: any) {
      setExportErr(e?.message ?? "Export failed");
    } finally {
      setExporting(null);
    }
  }

  const Icon = def.icon;

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto pb-16">
      <Link to="/reports" className="text-text-muted text-sm flex items-center gap-1 hover:text-text mb-2">
        <ArrowLeft size={14} /> Reports hub
      </Link>

      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
            <Icon size={20} className="text-accent" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{def.label}</h1>
            <p className="text-[13px] text-text-muted">{def.blurb}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" icon={FileDown} disabled={!!exporting} onClick={exportCsvFull}>
            {exporting === "csv" ? "Exporting…" : "CSV"}
          </Button>
          <Button variant="primary" size="sm" icon={Download} disabled={!!exporting} onClick={exportXlsx}>
            {exporting === "xlsx" ? "Building Excel…" : "Export Excel"}
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="mb-4">
        <div className="p-3 flex items-end gap-3 flex-wrap">
          {(def.searchFields.length > 0 || def.searchEmbed) && (
            <div className="flex-1 min-w-[200px]">
              <label className="block text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Search</label>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={`Find in ${def.label.toLowerCase()}…`}
                  className="w-full bg-ink border border-line rounded pl-8 pr-2.5 py-2 text-[13px] text-text placeholder:text-text-dim"
                />
              </div>
            </div>
          )}
          {def.dateField && (
            <>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">
                  {def.dateField.label} from
                </label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">to</label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text" />
              </div>
            </>
          )}
          {def.statusField && (
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">
                {def.statusField.label}
              </label>
              <select value={status} onChange={e => setStatus(e.target.value)}
                className="bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text">
                <option value="">All</option>
                {def.statusField.options.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
          {hasFilters && (
            <button
              onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setStatus(""); }}
              className="text-[12px] text-text-muted hover:text-lost inline-flex items-center gap-1 pb-2.5"
            >
              <X size={12} /> Clear
            </button>
          )}
          <div className="ml-auto pb-2 text-[12px] text-text-muted">
            {isLoading ? <Spinner size={13} /> : (
              <>
                <span className="text-text font-semibold">{(data?.count ?? 0).toLocaleString()}</span> rows match
                {(data?.count ?? 0) > PREVIEW_ROWS && <> · showing first {PREVIEW_ROWS}</>}
              </>
            )}
          </div>
        </div>
      </Card>

      {exportErr && (
        <Card className="mb-4 border-danger/30">
          <div className="p-3 flex items-center gap-2 text-sm text-danger">
            <AlertTriangle size={15} /> {exportErr}
          </div>
        </Card>
      )}

      {/* Preview table */}
      <Card>
        {error ? (
          <div className="p-6 flex items-start gap-3 text-sm">
            <AlertTriangle size={16} className="text-danger flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-danger">Couldn't load this dataset</div>
              <div className="text-text-muted mt-1">{(error as Error).message}</div>
            </div>
          </div>
        ) : isLoading ? (
          <div className="p-12 flex justify-center"><Spinner size={20} /></div>
        ) : previewRows.length === 0 ? (
          <div className="p-6">
            <EmptyState title="No rows" description={hasFilters ? "Nothing matches those filters — try widening them." : "Nothing captured in this dataset yet."} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-line bg-ink-raised">
                  {def.columns.map(c => (
                    <th
                      key={c.key}
                      onClick={() => setSort(s => s?.key === c.key ? (s.asc ? { key: c.key, asc: false } : null) : { key: c.key, asc: true })}
                      className={`px-3 py-2.5 font-semibold text-[10.5px] uppercase tracking-wider2 text-text-muted cursor-pointer select-none hover:text-text whitespace-nowrap ${
                        c.type === "money" || c.type === "num" ? "text-right" : "text-left"
                      }`}
                      title="Click to sort the preview"
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {sort?.key === c.key && (sort.asc ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((r, i) => (
                  <tr key={i} className="border-b border-line/50 hover:bg-ink-raised/60">
                    {def.columns.map(c => (
                      <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${
                        c.type === "money" || c.type === "num" ? "text-right font-mono" : ""
                      } ${c.type === "badge" ? "" : "text-text"}`}>
                        {c.type === "badge" && r[c.key]
                          ? <Pill label={String(r[c.key]).replace(/_/g, " ")} small />
                          : <span className={c.type === "text" ? "block max-w-[280px] truncate" : ""} title={c.type === "text" ? String(r[c.key] ?? "") : undefined}>
                              {fmtCell(r[c.key], c.type)}
                            </span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="mt-3 text-[11px] text-text-dim italic">
        Preview shows the first {PREVIEW_ROWS} rows (click a heading to sort them). Exports pull the <span className="text-text-muted">full filtered set</span> — Excel lands with an autofilter row so you can slice further there.
      </div>
    </div>
  );
}
